<script setup lang="ts">
import api from "@/api";
import { DateInstance, SimplifiedDate } from "@/assets/js/dateHelper";
import type {
	TPagination,
	TSchoolSuperAdminView,
} from "@/assets/js/globalTypes";

import {
	debounce,
	getButtonSeverityFromStatus,
	useCan,
} from "@/assets/js/helpers";
import {
	createNewSchoolModal,
	openConfirmDeleteModal,
	openImpersonateAdminModal,
} from "@/components/modals/modalDefinitions";
import { $t } from "@/i18n";
import { useDefaultStore } from "@/store";
import { useSchoolStore } from "@/store/school";
import { useToastStore } from "@/store/toast";

import type { DataTablePageEvent } from "primevue/datatable";
defineOptions({
	name: "admin.schools",
});
definePage({
	name: "admin.schools",
	meta: {
		hasKeepAlive: ["admin.schools.details"],
	},
});

const can = useCan();
const router = useRouter();
const schoolStore = useSchoolStore();
const store = useDefaultStore();

const route = useRoute();
const currentPage = computed(() => {
	return route.query.page;
});

const schools = ref<TSchoolSuperAdminView[]>([]);

const pagination = ref<TPagination>();

const rows = ref(10);
function changePage(event: DataTablePageEvent) {
	router.push({
		query: {
			...route.query,
			page: event.page + 1,
		},
	});
}

const schoolName = computed({
	get() {
		return route.query.name as string;
	},
	set(val) {
		router.replace({
			query: {
				...route.query,
				name: val || undefined,
				page: 1,
			},
		});
		getSchoolsDebounced();
	},
});

watch(
	() => currentPage.value,
	() => {
		getSchools();
	},
	// {
	// 	immediate: true,
	// },
);

async function getSchools() {
	try {
		const params = {
			page: Number(currentPage.value) || 1,
			per_page: rows.value || 20,
			scl_name: schoolName.value,
		};
		const response = await schoolStore.fetchSchools(params);
		for (const school of response.data) {
			if (school.subscription) {
				school.subscription.sbr_start = SimplifiedDate.fromUtcToLocal(
					school.subscription.sbr_start,
				);
				school.subscription.sbr_end = SimplifiedDate.fromUtcToLocal(
					school.subscription.sbr_end,
				);
			}
		}
		schools.value = response.data;
		pagination.value = response.meta.pagination;
	} catch (error) {
		console.error(error);
	}
}

const getSchoolsDebounced = debounce(getSchools, 500, false);

onActivated(() => {
	getSchools();
});
onBeforeMount(() => {
	getSchools();
});

function createNewSchool() {
	createNewSchoolModal(getSchools);
}

function resetPage() {
	router.replace({
		query: {
			...route.query,
			page: 1,
		},
	});
}

function updateRows(row) {
	if (rows.value === row) {
		return;
	}
	rows.value = row;
	if (Number(currentPage.value) === 1) {
		getSchools();
	} else {
		resetPage();
	}
}

async function handleImpersonate(data: TSchoolSuperAdminView) {
	const res = await api.getSchoolAdmins({ scl_id: data.scl_id });
	openImpersonateAdminModal(data.scl_id, res.data.data);
}

function deleteSchool(item: TSchoolSuperAdminView) {
	if (disableDelete.value) return;
	openConfirmDeleteModal({
		name: item.scl_name,
		deactivate: false,
		data: item,
		onConfirm: () => {
			callDeleteSchoolApi(item);
		},
	});
}

const disableDelete = ref(false);
const toastStore = useToastStore();
async function callDeleteSchoolApi(item: TSchoolSuperAdminView) {
	disableDelete.value = true;
	try {
		const res = await schoolStore.deleteSchool(item.scl_id);
		toastStore.openToastSuccess(res.message);
		getSchools();
	} catch (error) {
		console.error(error);
		toastStore.openToastError(error.response.data.message);
	} finally {
		disableDelete.value = false;
	}
}
</script>

<template>
	<div id="admin-schools">
		<p-card class="controller-wrap">
			<template #title>
				<div class="title-wrap">
					<h1>{{ $t("app.admin.nav.schools") }}</h1>

					<div class="input-wrap">
						<PIconField>
							<p-input-icon>
								<i-mdi-search></i-mdi-search>
							</p-input-icon>
							<PInputText
								v-model="schoolName"
								:placeholder="$t('app.base.search')"
								type="text"
							/>
						</PIconField>
						<PButton
							v-if="can('create-schools')"
							@click="createNewSchool"
						>
							{{ $t("app.base.create") }}
						</PButton>
					</div>
				</div>
			</template>
			<template #content>
				<PDataTable
					:value="schools"
					paginator
					:rows="rows"
					:first="(Number(currentPage) - 1 || 0) * (rows || 10)"
					table-style="width: 100%"
					:total-records="pagination?.total"
					lazy
					paginator-template="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
					:current-page-report-template="$t('app.base.pagination')"
					:rows-per-page-options="[10, 20, 50, 100]"
					striped-rows
					@update:rows="updateRows"
					@page="changePage"
				>
					<PColumn
						field="scl_name"
						:header="$t('app.base.name')"
					/>

					<PColumn
						field="nature"
						:header="$t('app.school.natures')"
					>
						<template #body="slotProps">
							{{
								slotProps.data.nature
									?.map((nature) => nature.tag_name)
									.join(", ")
							}}
						</template>
					</PColumn>
					<p-column :header="$t('app.subscription.confirmedPrice')">
						<template #body="slotProps">
							<template v-if="slotProps.data.subscription">
								{{
									slotProps.data.subscription.sbr_price + store.currencySymbol
								}}
							</template>
							<template v-else>
								{{ $t("app.subscription.notSubscribed") }}
							</template>
						</template>
					</p-column>
					<p-column :header="$t('app.subscription.endDate')">
						<template #body="slotProps">
							<template v-if="slotProps.data.subscription">
								{{
									DateInstance.getFormattedDate(
										slotProps.data.subscription.sbr_end,
										$t("app.dates.medium"),
									)
								}}
							</template>
							<template v-else>
								{{ $t("app.subscription.notSubscribed") }}
							</template>
						</template>
					</p-column>

					<p-column :header="$t('app.base.status')">
						<template #body="slotProps">
							<p-button
								outlined
								:severity="
									getButtonSeverityFromStatus(
										slotProps.data.subscription?.sbr_status,
									)
								"
								style="white-space: nowrap"
								as="router-link"
								:to="{
									name: 'admin.schools.details',
									params: { id: slotProps.data.scl_id },
								}"
							>
								{{
									(slotProps.data.subscription?.sbr_status &&
										$t(
											"app.subscription.status." +
												slotProps.data.subscription.sbr_status.toLowerCase(),
										)) ||
									$t("app.subscription.notSubscribed")
								}}
							</p-button>
						</template>
					</p-column>
					<p-column :header="$t('app.admin.impersonate')">
						<template #body="slotProps">
							<p-button
								outlined
								severity="info"
								style="white-space: nowrap"
								@click="handleImpersonate(slotProps.data)"
							>
								{{ $t("app.admin.impersonate") }}
							</p-button>
						</template>
					</p-column>
					<p-column :header="$t('app.base.details')">
						<template #body="slotProps">
							<router-link
								:to="{
									name: 'admin.schools.details',
									params: { id: slotProps.data.scl_id },
								}"
							>
								<i-ic-outline-info class="large" />
							</router-link>
						</template>
					</p-column>
					<p-column
						v-if="can('delete-schools')"
						:header="$t('app.base.delete')"
					>
						<template #body="slotProps">
							<i-ic-outline-cancel
								class="large danger"
								:class="{ disabled: disableDelete }"
								@click="deleteSchool(slotProps.data as TSchoolSuperAdminView)"
							/>
						</template>
					</p-column>
				</PDataTable>
			</template>
		</p-card>
	</div>
</template>

<style scoped lang="scss">
#admin-schools {
	.title-wrap {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
	.content-wrap {
		overflow: auto;
	}
	.controller-wrap {
		max-height: 100%;
	}
	.input-wrap {
		display: flex;
		gap: 10px;
		font-size: 1rem;
	}
	.light-button {
		margin: 10px 0;
	}
}
</style>
